import {
  Button,
  Field,
  FloatingTooltip,
  Textarea,
  TooltipCore,
  Text,
} from '@payaca/ambrosia';
import { useSpeechRecognition } from '@payaca/hooks';
import UntitledIcon from '@payaca/untitled-icons';
import {
  type FC,
  KeyboardEventHandler,
  RefAttributes,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useIntercom } from 'react-use-intercom';

import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';

import type { ChatMessage } from './types';

type Props = {
  onSend: (message: string) => void;
  /** Interrupt the in-flight response without sending anything. */
  onStop: () => void;
  /** Interrupt the in-flight response and send this message as the next turn. */
  onInterruptAndSend: (message: string) => void;
  isStreaming: boolean;
  disabled?: boolean;
  messages: ChatMessage[];
  placeholder?: string;
} & RefAttributes<HTMLTextAreaElement>;

// Intercom's composer chokes on very long pre-filled messages, so we cap the
// transcript we hand it. Keeping the newest messages (clip from the oldest end)
// preserves the most relevant context for the human agent picking up the chat.
const MAX_TRANSCRIPT_LENGTH = 5000;

function formatMessage(message: ChatMessage): string {
  const speaker = message.role === 'user' ? 'Me' : 'Dave';
  const text =
    message.role === 'assistant' && message.contentBlocks
      ? message.contentBlocks
          .filter((b) => b.type === 'text')
          .map((b) => b.text)
          .join('')
      : message.content;
  return `${speaker}: ${text}`;
}

// Keep the tail (most recent content) of an over-long message, marking the cut.
function clipToTail(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  const marker = '…';
  return marker + text.slice(text.length - (maxLength - marker.length));
}

function formatTranscript(messages: ChatMessage[]): string {
  const entries = messages.filter((m) => !m.isStreaming).map(formatMessage);

  // Accumulate from the most recent message backwards until we hit the cap.
  const kept: string[] = [];
  let length = 0;
  let droppedEarlier = false;
  for (let i = entries.length - 1; i >= 0; i--) {
    const entry = entries[i];
    // +2 accounts for the '\n\n' separator between entries.
    const nextLength = length + entry.length + (kept.length > 0 ? 2 : 0);
    if (nextLength > MAX_TRANSCRIPT_LENGTH) {
      if (kept.length === 0) {
        // The most recent message alone exceeds the cap. Clip it in place so we
        // never hand Intercom a message longer than the limit. Whole earlier
        // messages were dropped only if any precede this one.
        kept.unshift(clipToTail(entry, MAX_TRANSCRIPT_LENGTH));
        droppedEarlier = i > 0;
      } else {
        // This entry and everything before it was dropped whole.
        droppedEarlier = true;
      }
      break;
    }
    kept.unshift(entry);
    length = nextLength;
  }

  const transcript = kept.join('\n\n');
  return droppedEarlier
    ? `[earlier messages omitted]\n\n${transcript}`
    : transcript;
}

export const ChatInput: FC<Props> = (props) => {
  const {
    ref,
    onSend,
    onStop,
    onInterruptAndSend,
    isStreaming,
    disabled = false,
    messages,
    placeholder = 'Ask Dave...',
  } = props;
  const { show, showNewMessage } = useIntercom();
  const { data: regionPreferences } = useGetMyRegionPreferences();

  const [value, setValue] = useState('');

  const appendTranscriptToValue = useCallback((text: string) => {
    setValue((prev) => (prev ? `${prev} ${text}` : text));
  }, []);
  const {
    isListening,
    isSupported,
    interimTranscript,
    stop: stopListening,
    toggle: toggleListening,
  } = useSpeechRecognition({
    onTranscript: appendTranscriptToValue,
    lang: regionPreferences?.locale,
  });

  let displayValue = value;
  if (interimTranscript != null) {
    displayValue = value ? `${value} ${interimTranscript}` : interimTranscript;
  }

  // Dictation is unavailable while Dave is replying, so the mic never competes
  // with the interrupt controls that take its place mid-response.
  const canListen = !disabled && !isStreaming;

  // Stop an in-progress dictation the moment it becomes unavailable, rather than
  // leaving the user talking into a control they can no longer submit from.
  useEffect(() => {
    if (!canListen && isListening) {
      stopListening();
    }
  }, [canListen, isListening, stopListening]);

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    // Enter without modifier keys submits (like Slack)
    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !e.ctrlKey &&
      !e.altKey &&
      !e.metaKey
    ) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const hasText = !!displayValue.trim();

  // Default submit: send normally, or queue for the next turn while streaming.
  const handleSubmit = () => {
    if (hasText && !disabled) {
      stopListening();
      onSend(displayValue.trim());
      setValue('');
    }
  };

  // Interrupt the current response and send this message immediately.
  const handleInterruptAndSend = () => {
    if (hasText && !disabled) {
      stopListening();
      onInterruptAndSend(displayValue.trim());
      setValue('');
    }
  };

  const micLabel = isListening ? 'Stop listening' : 'Speak to Dave';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <Field.Legacy className="relative">
        <Textarea
          ref={ref}
          className="resize-none pb-11"
          autoHeight
          rows={1}
          maxRows={10}
          placeholder={isListening ? 'Dave is listening...' : placeholder}
          value={displayValue}
          onChange={setValue}
          onKeyDown={handleKeyDown}
          autoFocus
        />

        <FloatingTooltip placement="right">
          <Button
            aria-label="Speak to a human"
            className="absolute bottom-2 left-2 m-0"
            variant="quaternary"
            size="xs"
            disabled={disabled}
            onClick={() => {
              const hasUserMessages = messages.some((m) => m.role === 'user');
              if (hasUserMessages) {
                showNewMessage(formatTranscript(messages));
              } else {
                show();
              }
            }}
          >
            <UntitledIcon name="help-circle.3" className="size-5" />
          </Button>

          <TooltipCore>Speak to a human</TooltipCore>
        </FloatingTooltip>

        <div className="absolute bottom-2 right-2 flex items-center gap-1">
          {isSupported && (
            <FloatingTooltip placement="top">
              <Button
                aria-label={micLabel}
                aria-pressed={isListening}
                className="m-0"
                variant="quaternary"
                colour={isListening ? 'negative' : undefined}
                size="xs"
                disabled={!canListen && !isListening}
                onClick={toggleListening}
              >
                <UntitledIcon
                  name={isListening ? 'stop-square.3' : 'microphone-01.3'}
                  className={
                    isListening
                      ? // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- No animation timing utility
                        'size-5 animate-pulse [animation-duration:1.5s]'
                      : 'size-5'
                  }
                />
              </Button>

              <TooltipCore>{micLabel}</TooltipCore>
            </FloatingTooltip>
          )}

          {isStreaming && (
            <FloatingTooltip placement="top">
              <Button
                aria-label="Stop Dave"
                className="m-0"
                variant="quaternary"
                colour="negative"
                size="xs"
                onClick={onStop}
              >
                <UntitledIcon name="stop-square.3" className="size-5" />
              </Button>

              <TooltipCore>Stop Dave (Esc)</TooltipCore>
            </FloatingTooltip>
          )}

          {isStreaming ? (
            hasText && (
              <>
                <FloatingTooltip placement="top">
                  <Button
                    aria-label="Stop Dave and send now"
                    className="m-0"
                    variant="quaternary"
                    size="xs"
                    onClick={handleInterruptAndSend}
                  >
                    <UntitledIcon name="flip-forward.3" className="size-5" />
                  </Button>

                  <TooltipCore>Stop Dave and send now</TooltipCore>
                </FloatingTooltip>

                <FloatingTooltip placement="top">
                  <Button
                    aria-label="Add message to queue"
                    className="m-0"
                    type="submit"
                    size="xs"
                  >
                    <UntitledIcon name="send-01.3" className="size-5" />
                  </Button>

                  <TooltipCore>Add to queue</TooltipCore>
                </FloatingTooltip>
              </>
            )
          ) : (
            <Button
              className="m-0"
              type="submit"
              size="xs"
              disabled={disabled || !hasText}
            >
              <UntitledIcon name="send-01.3" className="size-5" />
            </Button>
          )}
        </div>
      </Field.Legacy>
      <div className="mt-1 text-center">
        <Text level="supporting">
          Dave is AI and can make mistakes. Please double check responses.
        </Text>
      </div>
    </form>
  );
};
